import { useStripePaymentsEnabled } from "../../hooks/useStripePaymentsEnabled";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { ListBase, TextInput, useListContext, useRecordContext } from "react-admin";
import { Invoice } from "../../providers/invoicesProvider";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EuroSymbolIcon from "@mui/icons-material/EuroSymbol";
import { StripeIcon } from "../../icons/StripeIcon";
import { Money } from "../../misc/Money";
import sum from "lodash/sum";
import React, { useEffect } from "react";
import { UserEndpoint } from "../../generated/backendClient";
import { DateTime } from "luxon";
import { grants } from "../../backoffice.access_control";
import { RefundMultipleDialogButton } from "../../buttons/RefundMultipleDialogButton";
import { RefundPrepaidCreditsButton } from "../../buttons/RefundPrepaidCreditsButton";
import { getAuthenticatedBackendClient } from "../../api/backend.api";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useMenu } from "../../hooks/useMenu";
import { Student } from "../../providers/studentsProvider";
import { SlideUp } from "../../misc/SlideUp";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { FormProvider, useForm } from "react-hook-form";
import { EurosInput } from "../../inputs/MoneyInput";
import { Row } from "../../misc/Row";
import { LoadingButton } from "@mui/lab";
import type { PaymentModeEnum } from "../../generated/backendClient";

type CashBalanceTransaction = Awaited<
  ReturnType<UserEndpoint["userCashBalanceTransactionsRetrieve"]>
>["cash_balance_transactions"][0];

const moneyToCents = (money: { amount: string; currency: string }) => Math.round(parseFloat(money.amount) * 100);

function CashBalanceSummaryTable(props: { studentId: string }) {
  const enabled = useStripePaymentsEnabled();
  return enabled ? <EnabledCashBalanceSummaryTable {...props} /> : null;
}

function EnabledCashBalanceSummaryTable(props: { studentId: string }) {
  const { studentId } = props;
  const theme = useTheme();
  const [userCashBalanceTransactions, setUserCashBalanceTransactions] = React.useState<CashBalanceTransaction[]>([]);
  useEffect(() => {
    void (async () => {
      const backendClient = await getAuthenticatedBackendClient();
      const response = await backendClient.user.userCashBalanceTransactionsRetrieve({ id: studentId });
      setUserCashBalanceTransactions(response.cash_balance_transactions);
    })();
  }, [studentId]);
  const totalSum = sum(userCashBalanceTransactions.map((item) => moneyToCents(item.amount)));
  return (
    <table style={{ width: "100%", marginTop: 16, borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={{ textAlign: "left", padding: "8px 0" }}>Datum</th>
          <th style={{ textAlign: "right", padding: "8px 0" }}>Betrag</th>
        </tr>
      </thead>
      <tbody>
        {userCashBalanceTransactions.length === 0 && (
          <tr>
            <td colSpan={2} style={{ padding: "8px 0", color: theme.palette.text.secondary }}>
              Keine Transaktionen gefunden.
            </td>
          </tr>
        )}
        {userCashBalanceTransactions.map((tx) => (
          <tr key={tx.id} style={{ borderTop: `1px solid ${theme.palette.divider}` }}>
            <td style={{ padding: "8px 0" }}>{DateTime.fromISO(tx.created_at).toFormat("dd.MM.yyyy")}</td>
            <td style={{ textAlign: "right", padding: "8px 0" }}>
              <Money cents={moneyToCents(tx.amount)} />
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr style={{ borderTop: `2px solid ${theme.palette.text.primary}` }}>
          <td style={{ padding: "8px 0", fontWeight: "bold" }}>Gesamt</td>
          <td style={{ textAlign: "right", padding: "8px 0", fontWeight: "bold" }}>
            <Money cents={totalSum} />
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function PaymentModeIcon(props: { paymentMode: PaymentModeEnum | null; externalPaymentType: string | null }) {
  const { paymentMode, externalPaymentType } = props;
  if (paymentMode === "CASH") {
    if (externalPaymentType === "external_bank_transfer") {
      return (
        <Tooltip title="Banküberweisung">
          <AccountBalanceIcon fontSize="small" sx={{ color: "text.secondary", verticalAlign: "middle" }} />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Barzahlung">
        <EuroSymbolIcon fontSize="small" sx={{ color: "text.secondary", verticalAlign: "middle" }} />
      </Tooltip>
    );
  }
  if (paymentMode === "STRIPE_CARD_TERMINAL") {
    return (
      <Tooltip title="Kartenzahlung">
        <CreditCardIcon fontSize="small" sx={{ color: "text.secondary", verticalAlign: "middle" }} />
      </Tooltip>
    );
  }
  if (paymentMode?.startsWith("STRIPE")) {
    return (
      <Tooltip title="Stripe">
        <StripeIcon sx={{ color: "text.secondary", verticalAlign: "middle" }} fontSize="small" />
      </Tooltip>
    );
  }
  return null;
}

function PrepaidSummaryTable(props: { prepaidCredits: number }) {
  const { prepaidCredits } = props;
  const theme = useTheme();
  const context = useListContext();
  const invoices: Invoice[] = context?.data || [];
  const totalSum = sum(invoices.map((invoice) => invoice.total));
  return (
    <table style={{ width: "100%", marginTop: 16, borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={{ textAlign: "left", padding: "8px 0" }}>Datum</th>
          <th style={{ textAlign: "right", padding: "8px 0" }}>Betrag</th>
          <th style={{ textAlign: "left", padding: "8px 0", paddingLeft: "16px" }}>Nummer</th>
          <th style={{ padding: "8px 0" }}></th>
          <th style={{ padding: "8px 0" }}></th>
        </tr>
      </thead>
      <tbody>
        {invoices.length === 0 && (
          <tr>
            <td colSpan={5} style={{ padding: "8px 0", color: theme.palette.text.secondary }}>
              Keine Einzahlungen gefunden.
            </td>
          </tr>
        )}
        {invoices.map((invoice) => (
          <tr key={invoice.id} style={{ borderTop: `1px solid ${theme.palette.divider}` }}>
            <td style={{ padding: "8px 0" }}>{invoice.createdAt.toFormat("dd.MM.yyyy")}</td>
            <td style={{ textAlign: "right", padding: "8px 0" }}>
              <Money cents={invoice.total} />
              {!!invoice.refunded_amount && (
                <Typography variant="caption" display="block">
                  Erstattet: <Money cents={invoice.refunded_amount} />
                </Typography>
              )}
            </td>
            <td style={{ padding: "8px 0", paddingLeft: "16px" }}>{invoice.nr}</td>
            <td style={{ padding: "8px 4px" }}>
              <PaymentModeIcon paymentMode={invoice.payment_mode} externalPaymentType={invoice.external_payment_type} />
            </td>
            <td style={{ padding: "8px 0" }}>
              <Row sx={{ gap: 1, whiteSpace: "nowrap" }}>
                {invoice.downloadUrl && (
                  <Button
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => window.open(invoice.downloadUrl!, "_blank")}
                  >
                    PDF
                  </Button>
                )}
                <RefundPrepaidCreditsButton invoice={invoice} prepaidCredits={prepaidCredits} />
              </Row>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr style={{ borderTop: `2px solid ${theme.palette.text.primary}` }}>
          <td style={{ padding: "8px 0", fontWeight: "bold" }}>Gesamt</td>
          <td style={{ textAlign: "right", padding: "8px 0", fontWeight: "bold" }}>
            <Money cents={totalSum} />
          </td>
          <td colSpan={3}></td>
        </tr>
      </tfoot>
    </table>
  );
}

type AdjustPrepaidCreditsDialogFormValues = {
  money: number;
  reason: string;
};

function AdjustPrepaidCreditsDialog({
  isOpen,
  onCancel,
  studentId,
  drivingSchoolId,
}: {
  isOpen: boolean;
  onCancel: () => void;
  studentId: string;
  drivingSchoolId: string;
}) {
  const form = useForm<AdjustPrepaidCreditsDialogFormValues>({
    defaultValues: {
      money: 0,
    },
  });

  const onSubmit = async (formValues: AdjustPrepaidCreditsDialogFormValues) => {
    const backendClient = await getAuthenticatedBackendClient();
    try {
      await backendClient.user.userAdjustPrepaidCreditsCreate({
        id: studentId,
        requestBody: {
          reason: formValues.reason,
          money: { amount: formValues.money.toFixed(2), currency: "EUR" },
          driving_school_id: drivingSchoolId,
        },
      });
    } catch (error) {
      showErrorToast(`Fehler beim Anpassen der Prepaid Credits: ${(error as any).toString()}`);
      return;
    }
  };
  return (
    <FormProvider {...form}>
      <Dialog open={isOpen} onClose={onCancel} TransitionComponent={SlideUp}>
        <DialogTitle>Prepaid Credits anpassen</DialogTitle>
        <DialogCloseButton onClick={onCancel} />
        <DialogContent>
          <Row>Außerhalb des Systems eingezahlte Prepaid Credits können hier angepasst werden.</Row>
          <Row sx={{ mt: 2 }}>
            <Alert variant="outlined" severity="info">
              Ein positiver Betrag erhöht das Guthaben, ein negativer Betrag verringert es.
            </Alert>
          </Row>
          <Row sx={{ flexDirection: "row-reverse", mt: 1.5 }}>
            <TextInput
              label="Grund"
              autoFocus
              multiline
              minRows={1}
              source="reason"
              onChange={(event) => form.setValue("reason", event.target.value)}
              validate={(value) => (value ? null : "Grund ist erforderlich")}
              required
              fullWidth
            />
          </Row>
          <Row sx={{ flexDirection: "row-reverse", mb: 1 }}>
            <EurosInput
              label="Betrag"
              source="amount"
              sx={{ "& .MuiInputBase-root": { minWidth: "150px", width: "150px" } }}
              validate={(value) => {
                if (value === null) {
                  return "Betrag ist erforderlich";
                }
              }}
              onChange={(value) => form.setValue("money", value)}
            />
          </Row>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            variant="contained"
            onClick={form.handleSubmit(onSubmit)}
            loading={form.formState.isSubmitting}
          >
            Prepaid Credits anpassen
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}

export const PrepaidInvoicesCard = ({ studentId }: { studentId: string }) => {
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const [adjustPrepaidCreditsDialogOpen, setAdjustPrepaidCreditsDialogOpen] = React.useState(false);
  const closeDialog = () => {
    setAdjustPrepaidCreditsDialogOpen(false);
  };
  const student = useRecordContext<Student>();
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Einzahlungsübersicht
        </Typography>
        {student.balances.migratedPrepaidCredits > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Migriertes Prepaid-Guthaben aus Altsystem:
            </Typography>
            <Money cents={student.balances.migratedPrepaidCredits} />
          </Box>
        )}
        <CashBalanceSummaryTable studentId={studentId} />
        <ListBase resource="b2cInvoices" filter={{ studentId, type: "PREPAID_CREDITS" }} perPage={999}>
          <IconButton onClick={openMenu} data-testid="studentAdjustPrepaidCreditsButton" style={{ float: "right" }}>
            <MoreVertIcon />
          </IconButton>
          {grants.has("editStudentAdjustPrepaidCredits") && (
            <>
              <AdjustPrepaidCreditsDialog
                drivingSchoolId={student.drivingSchoolId}
                isOpen={adjustPrepaidCreditsDialogOpen}
                onCancel={closeDialog}
                studentId={studentId}
              />
              <Typography variant="h6" gutterBottom style={{ paddingTop: "40px" }}>
                Einzahlungen
              </Typography>
              <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    setAdjustPrepaidCreditsDialogOpen(true);
                  }}
                >
                  Guthaben anpassen
                </MenuItem>
              </Menu>
            </>
          )}
          <PrepaidSummaryTable prepaidCredits={student.balances.prepaidCredits} />
        </ListBase>
        {(grants.has("Invoice:manualRefund") || grants.has("PrepaidCredits:refund")) && (
          <>
            <Typography variant="h6" gutterBottom style={{ paddingTop: "20px" }}>
              Rückerstattungen
            </Typography>
            {grants.has("PrepaidCredits:refund") && (
              <Paper sx={{ p: 2 }}>
                <RefundMultipleDialogButton
                  studentId={studentId}
                  refundType="STRIPE_REFUND"
                  style={{ paddingBottom: "10px", paddingTop: "10px" }}
                />
              </Paper>
            )}
            {grants.has("Invoice:manualRefund") && (
              <Paper sx={{ p: 2, marginTop: "10px" }}>
                <RefundMultipleDialogButton
                  studentId={studentId}
                  refundType="MANUAL_REFUND"
                  style={{ paddingBottom: "10px", paddingTop: "10px" }}
                />
              </Paper>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
